import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { matchPath, useBeforeUnload, useBlocker, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FolderDetails, Folders, Layout, LoadingSection } from "../../components";
import { useAppDispatch, useAppSelector } from "../../redux";
import { resetQuoteSlice, updateQuoteSlice } from "../../redux/slices/quote";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { HasAccess } from "../../router/authorization";
import { PERMISSIONS } from "../../router/permission";
import { AppRoutes } from "../../router/routes";
import { BasicDetails } from "./basicDetails";
import { buildObjectForState, NavBars } from "./utils";
import { ItemDetails } from "./itemDetails";
import { useLazyGetQuotesByIdQuery, useLinkModuleMutation, useUnlinkModuleMutation, useUpdateQuoteMutation } from "../../redux/services";
import { QuoteCorrespondance } from "./correspondance";
import { QuoteCoverLetter } from "./coverLetter";
import { QuoteTermsAndConditions } from "./termsAndConditions";
import QuoteFormat from "./quoteFormat";
import { UserAccessManagement } from "../userAccessManagement";
import { QuoteContents } from "./contents";
import { BreadScrumHeaderHistoryProp } from "../../interfaces";
import CoverPage from "./coverPage";
import { QuoteReport } from "./quoteReport";

const ViewIEQuote: React.FC<{ children?: JSX.Element, currentPath: string, readOnly?: boolean, showLinkDocumentBtn?: boolean }> = ({ showLinkDocumentBtn = true, ...props }) => {

    const params = useParams();
    const navigate = useNavigate();

    const location = useLocation();
    const paths = location.state?.paths ?? [];
    const parentData = location.state?.parentData;
    const object_type = location.state?.object_type;

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [, setSearchParams] = useSearchParams();

    const [getQuoteById, quoteData] = useLazyGetQuotesByIdQuery();
    const [getQuoteByIdFoReport] = useLazyGetQuotesByIdQuery();
    const [updateQuote] = useUpdateQuoteMutation();
    const [linkDocument] = useLinkModuleMutation();
    const [unlinkDocument] = useUnlinkModuleMutation();

    const [currentPath, setCurrentPath] = React.useState<string>(props.currentPath);
    const [history, setHistory] = React.useState<BreadScrumHeaderHistoryProp[]>([]);

    const quoteId = params?.id ?? "New";

    const isEdit = !['New', ':id'].includes(quoteId);

    const { perm } = useAppSelector(store => store.auth.userDetails);
    const { quote } = useAppSelector(store => store);

    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_CREATE), [perm]);
    const readable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_READ), [perm]);
    const updatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_UPDATE), [perm]);
    const deletable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_DELETE), [perm]);
    const publishable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_PUBLISH), [perm]);

    const readOnly = isEdit ? !updatable : !creatable;

    const [isSaved, setIsSaved] = React.useState<boolean>(true);
    const [hideNavBar, setHideNav] = React.useState<boolean>(false);

    const callLinkDocument = async ({ links = [], refetch, folderDetails }: { links?: string[], refetch?: any, folderDetails?: FolderDetails }) => {
        dispatch(openBackdrop(`Linking Document${links.length === 1 ? "" : "s"}...`));

        await Promise.all(
            [...links].map(
                (doc_id: any) => {
                    let folderAssociation = folderDetails ? [{ object_ids: [folderDetails.object_id], object_type: folderDetails.object_type }] : []
                    return linkDocument({
                        object_type: "document", object_id: doc_id, payload: {
                            links: [
                                ...folderAssociation,
                                { object_ids: [quoteId], object_type: "quote" }
                            ]
                        }
                    })
                }
            )
        )
            .then(res => {
                const errorRes: any[] = res?.filter((r: any) => r?.error) ?? [];
                if (errorRes?.length > 0) {
                    enqueueSnackbar(`Unable to link the document${links.length === 1 ? "" : "s"}. Info: ${errorRes[0]?.error?.data?.title ?? errorRes[0]?.error?.data ?? "Unknown"}`, { variant: "error" })
                } else {
                    enqueueSnackbar(`Document${links.length === 1 ? "" : "s"} Linked Successfully!!!`, { variant: "success" });
                }
                if (refetch) {
                    refetch();
                }
            }).catch((err) => {
                enqueueSnackbar(`Unable to link the document${links.length === 1 ? "" : "s"}. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    };

    const callUnLinkDocument = async ({ unlinks, refetch, row }: { unlinks: string[], refetch?: any, row?: any }) => {
        dispatch(openBackdrop(`Unlinking Document${unlinks.length === 1 ? "" : "s"}...`));
        const filteredFolder = row?.associations?.find((a: any) => a?.object_type === "folder")
        await Promise.all(
            unlinks.map(
                (doc_id: any) => {
                    let folderAssociation = filteredFolder ? [{ object_ids: [filteredFolder?.object_details?.[0]?._id], object_type: filteredFolder?.object_type }] : []
                    return unlinkDocument({
                        object_type: "document", object_id: doc_id, payload: {
                            unlinks: [
                                ...folderAssociation,
                                { object_ids: [quoteId], object_type: "quote" }
                            ]
                        }
                    })
                }
            )
        )
            .then(res => {
                const errorRes: any[] = res?.filter((r: any) => r?.error) ?? [];
                if (errorRes?.length > 0) {
                    enqueueSnackbar(`Unable to unlink the document${unlinks.length === 1 ? "" : "s"}. Info: ${errorRes[0]?.error?.data?.title ?? errorRes[0]?.error?.data ?? "Unknown"}`, { variant: "error" })
                } else {
                    enqueueSnackbar(`Document${unlinks.length === 1 ? "" : "s"} Unlinked Successfully!!!`, { variant: "success" });
                }
                if (refetch) {
                    refetch();
                }
            }).catch((err) => {
                enqueueSnackbar(`Unable to unlink the document${unlinks.length === 1 ? "" : "s"}. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    }

    const onRegenerate = async () => {
        await updateQuote({ id: quoteId, payload: undefined, update_version: false }).then((res: any) => {
            if (res.error) {
                enqueueSnackbar(res?.error?.data?.title ?? `Something went wrong, Unable to update the quote.`, { variant: "error" });
            } else {
                getQuoteByIdFoReport(quoteId);
            }
        }).catch(err => {
            enqueueSnackbar(err?.data?.internal_error ?? err?.data?.title ?? `Something went wrong, Unable to update the quote.`, { variant: "error" });
        });
    };

    const giveMeAssociations = useMemo(() => {
        return quoteData?.data?.associations
    }, [quoteData.data?.associations]);


    const giveMeComponent = () => {
        // Basic Details
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteBasicDetail(quoteId))) {
            return <BasicDetails isEdit={isEdit} quoteId={quoteId} refetch={fetchIEQuotes} readOnly={readOnly} setIsSaved={setIsSaved} />
        }

        // Item Details
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteItemDetails(quoteId))) {
            return <ItemDetails quoteId={quoteId} readOnly={readOnly || quote?.released} setIsSaved={setIsSaved} refetch={fetchIEQuotes} />
        }

        // Correspondance
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteCorrespondance(quoteId))) {
            return <QuoteCorrespondance quoteId={quoteId} readOnly={readOnly || quote?.released} setIsSaved={setIsSaved} refetch={fetchIEQuotes} />
        }

        // Cover Page
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteCoverPage(quoteId))) {
            return <CoverPage quoteId={quoteId} readOnly={readOnly || quote?.released} setIsSaved={setIsSaved} refetch={fetchIEQuotes} />
        }

        // Cover Letter
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteCoverLetter(quoteId))) {
            return <QuoteCoverLetter quoteId={quoteId} readOnly={readOnly || quote?.released} setIsSaved={setIsSaved} refetch={fetchIEQuotes} />
        }

        // Contents
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteContentsSection(quoteId))) {
            return <QuoteContents quoteId={quoteId} readOnly={readOnly || quote?.released} setIsSaved={setIsSaved} refetch={fetchIEQuotes} />
        }

        // Terms & Conditions
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteTandC(quoteId))) {
            return <QuoteTermsAndConditions quoteId={quoteId} readOnly={readOnly || quote?.released} setIsSaved={setIsSaved} refetch={fetchIEQuotes} />
        }

        // Documents
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteDocuments(quoteId))) {

            return <Folders
                onRegenerate={onRegenerate}
                object_id={quoteId}
                object_type={'quote'}
                object_sequence_id={quoteData?.data?.sequence_id}
                object_association={giveMeAssociations}
                updateHistory={(newHistory, currentPath) => {
                    updateBreadScrumHistory([
                        ...newHistory.map(_ => ({ label: _.label, onClick: () => navigate(location.pathname) }))
                    ])
                    setCurrentPath(currentPath)
                }}
                updateCurrentPath={(path) => setCurrentPath(path)}
                creatable={!quote?.released && creatable}
                updatable={!quote?.released && updatable}
                deletable={!quote?.released && deletable}
                readable={readable}
                publishable={!quote?.released && publishable}
                linkDocument={(links: any, refetch: any, unlinks: string[], type: string, folderDetail: any) => {
                    let folderDetails = folderDetail?.object_type === "folder" ? folderDetail : undefined;
                    callLinkDocument({ links, refetch, folderDetails })
                }}
                isPdfOnly={true}
                unlinkDoc={(id: string, refetch: any, row) => callUnLinkDocument({ unlinks: [id], refetch, row })}
                showHelperBtn={object_type ? showLinkDocumentBtn : false}
                showCreateBtn={object_type ? false : true}
                parentData={parentData}
                showDeleteBtn={!quote?.released && deletable}
            />
        }

        // Quote Format
        if (matchPath(location.pathname, AppRoutes.viewQuickQuotePreview(quoteId))) {
            if (quote.id) {
                return <QuoteFormat quoteId={quoteId} readOnly={readOnly} loading={quoteData.isLoading || quoteData.isFetching} />
            } else {
                return <LoadingSection message="Preparing Quote Preview..." />
            }
        }

        // Report Generation
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteReportGeneration(quoteId))) {
            return <QuoteReport quoteId={quoteId} readOnly={readOnly} loading={quoteData.isLoading || quoteData.isFetching} refetch={getQuoteByIdFoReport} />
        }

        // User Management
        if (matchPath(location.pathname, AppRoutes.manageUsersOnQuote(quoteId))) {
            return <UserAccessManagement updatable={updatable} isLoading={quoteData.isFetching || quoteData.isLoading} object_id={quoteId} object_type="quote" refetch={fetchIEQuotes} user_access={quoteData?.data?.user_access} sequence_id={quoteData?.data?.sequence_id ?? "..."} created_user={quoteData?.data?.created_user} />
        }

        return <></>
    };

    const showError = () => {
        enqueueSnackbar('404 - Unable to find the requested Quote details', { variant: "error" });
        dispatch(openConfirmationDialog({
            title: "404 - Not Found",
            body: "Unable to find the requested Quote details",
            positiveBtn: "Ok",
            onOk: () => {
                dispatch(closeConfirmationDialog())
                navigate(AppRoutes.allQuickQuotes)
            },
            hideNegativeBtn: true
        }))
    };

    const fetchIEQuotes = async () => {
        dispatch(resetQuoteSlice());
        if (isEdit) {
            try {
                dispatch(openBackdrop("Fetching Quote Details..."));

                const response = await getQuoteById(quoteId);

                if (response.data) {
                    let newState = buildObjectForState(response);
                    dispatch(updateQuoteSlice(newState));
                    updateBreadScrumHistory();
                } else {
                    showError();
                }
            } catch (error) {
                console.log('error:', error)
                showError();
            } finally {
                dispatch(closeBackdrop());
            }
        }
    };

    // Block navigating elsewhere when data has been entered into the input for router navigation
    let blocker = useBlocker(({ currentLocation, nextLocation }) => !isSaved && currentLocation.pathname !== nextLocation.pathname);

    // Block navigating elsewhere when data has been entered into the input for browser
    useBeforeUnload(React.useCallback((event) => {
        if (!isSaved) {
            event.preventDefault()
            // eslint-disable-next-line no-param-reassign
            event.returnValue = "Changes that you made may not be saved."
        }
        // eslint-disable-next-line
    }, [quote]), { capture: true });

    const updateBreadScrumHistory = (appendHistory: BreadScrumHeaderHistoryProp[] = []) => {
        let breadScrumHistory: BreadScrumHeaderHistoryProp[] = [
            { label: "Commercial Module", onClick: () => navigate(AppRoutes.allQuickQuotes) },
            { label: "Quick Quote", onClick: () => navigate(AppRoutes.allQuickQuotes) },
            { label: `${(quoteData.isLoading || quoteData.isFetching) ? "Loading..." : `${(quoteData?.data?.sequence_id ?? quote?.sequence_id) || "New"}${isEdit ? `-r${quoteData?.data?.version ?? quote?.version ?? ""}` : ""}`}`, onClick: () => navigate(AppRoutes.viewQuickQuoteBasicDetail(quoteId)) }
        ];

        if (paths?.length > 0) {
            breadScrumHistory = [
                ...(paths ?? []),
                breadScrumHistory[1]
            ]
        }

        setHistory([...breadScrumHistory, ...appendHistory]);
    };

    React.useEffect(() => {
        setCurrentPath(props.currentPath);
        updateBreadScrumHistory();
        // eslint-disable-next-line
    }, [props.currentPath, quoteData.status, JSON.stringify(quote)]);

    React.useEffect(() => {
        updateBreadScrumHistory();
        dispatch(resetQuoteSlice());
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        if (blocker && blocker.state === 'blocked') {
            dispatch(openConfirmationDialog({
                title: "Do you want to leave this page?",
                body: "Changes that you made may not be saved.",
                positiveBtn: "Save",
                forceBtn: "Leave",
                negativeBtn: "Cancel",
                hideForceBtn: false,
                onForce: () => {
                    if (blocker.proceed) {
                        blocker.proceed();
                    }
                    dispatch(closeConfirmationDialog());
                },
                onNegativeBtn: () => {
                    if (blocker.reset) {
                        blocker.reset();
                    }
                    dispatch(closeConfirmationDialog());
                },
                onOk: () => {
                    if (blocker.reset) {
                        blocker.reset();
                    }
                    setSearchParams({ forceSave: "true" });
                    dispatch(closeConfirmationDialog());
                }
            }));
        }
        // eslint-disable-next-line
    }, [blocker]);

    React.useEffect(() => {
        if (quoteId === ':id') {
            dispatch(closeBackdrop());
            navigate(AppRoutes.allQuickQuotes)
        }
    });

    React.useEffect(() => {
        if (isEdit) {
            fetchIEQuotes();
        }
        // eslint-disable-next-line    
    }, [quoteId]);

    React.useEffect(() => {
        if (matchPath(location.pathname, AppRoutes.viewQuickQuoteCoverPage(quoteId))) {
            setHideNav(true);
        } else {
            setHideNav(false);
        }
        // eslint-disable-next-line    
    }, [location.pathname]);

    return (
        <Box
            sx={{
                p: 0.5,
                height: "99.8%",
                width: "99.8%"
            }}>
            <Layout
                history={history}
                currentPath={currentPath}
                navBars={NavBars(quoteId, isEdit)}
                mainPanel={giveMeComponent()}
                sideNavVariant={"whiteboard"}
                hideNavBarByDefault={hideNavBar}
                locationState={location?.state}
                resizable
            />
        </Box>
    );
}

export default ViewIEQuote;